// ═══════════════════════════════════════════════════════════════
// GIFT TYPES CONSTANTS
// Path: src/constants/giftTypes.js
//
// Single source of truth for all gift-related enumerations,
// IRS acknowledgment language, and display helpers.
// ═══════════════════════════════════════════════════════════════

// ── Gift Types ───────────────────────────────────────────────────
export const GIFT_TYPES = [
  {
    id: "cash",
    label: "Check / Cash / Credit Card",
    shortLabel: "Cash",
    description: "Standard monetary gift by check, cash, or credit card",
    hasAmount: true,
    hasPaymentMethod: true,
    hasGoodsServices: true,
    hasFund: true,
  },
  {
    id: "inkind",
    label: "In-Kind Gift",
    shortLabel: "In-Kind",
    description: "Non-cash donation of goods or services",
    hasAmount: false,
    hasPaymentMethod: false,
    hasGoodsServices: false,
    hasFund: true,
    requiresDescription: true,
  },
  {
    id: "stock",
    label: "Stock / Securities",
    shortLabel: "Stock",
    description: "Transfer of shares or securities",
    hasAmount: false,
    hasPaymentMethod: false,
    hasGoodsServices: false,
    hasFund: true,
    requiresTicker: true,
  },
  {
    id: "daf",
    label: "Donor Advised Fund",
    shortLabel: "DAF",
    description: "Grant from a DAF sponsoring organization",
    hasAmount: true,
    hasPaymentMethod: false,
    hasGoodsServices: false,
    hasFund: true,
    requiresDafOrg: true,
  },
];

export const GIFT_TYPE_MAP = Object.fromEntries(
  GIFT_TYPES.map((t) => [t.id, t])
);

// ── Payment Methods ───────────────────────────────────────────────
export const PAYMENT_METHODS = [
  { id: "check", label: "Check" },
  { id: "cash", label: "Cash" },
  { id: "credit_card", label: "Credit Card" },
  { id: "daf", label: "DAF" },
  { id: "qcd", label: "QCD" },
  { id: "ira", label: "IRA" },
  { id: "inkind_stock", label: "In-Kind (Stocks)" },
  { id: "hist", label: "Historical Import" },
];

export const PAYMENT_METHOD_MAP = Object.fromEntries(
  PAYMENT_METHODS.map((m) => [m.id, m])
);

// ── Acknowledgment Channels ───────────────────────────────────────
export const ACKNOWLEDGMENT_CHANNELS = [
  { id: "email", label: "Email" },
  { id: "mail", label: "Mailed Letter" },
  { id: "in_person", label: "In Person" },
  { id: "none", label: "None / Waived" },
];

// ── IRS Acknowledgment Language ───────────────────────────────────
// Indexed by gift type. Used in GiftReceiptTemplate.
// {orgName} and {giftYear} are replaced at render time.
// {fmv}, {deductible}, {description} for quid pro quo.
// {dafOrg} for DAF acknowledgments.

export const IRS_LANGUAGE = {
  // Cash / CC / Check — no goods or services
  cash_simple:
    "No goods or services were provided in exchange for this contribution.",

  // Cash / CC / Check — with goods or services (quid pro quo)
  cash_quid_pro_quo:
    'In connection with your gift of ${amount}, goods or services described as "{description}" ' +
    "with a fair market value of ${fmv} were provided to you by {orgName}. " +
    "The deductible portion of your contribution is ${deductible}.",

  // In-Kind
  inkind:
    "No goods or services were provided in exchange for this contribution. " +
    "{orgName} has not assigned a value to this gift. " +
    "You are solely responsible for determining the fair market value of this contribution " +
    "for tax deduction purposes. Please consult your tax advisor.",

  // Stock / Securities
  stock:
    "No goods or services were provided in exchange for this contribution. " +
    "No value has been assigned by {orgName}. " +
    "The deductible amount is the fair market value of the securities on the date of transfer, " +
    "as determined by you or your financial advisor.",

  // Donor Advised Fund — acknowledgment only, not a receipt
  daf:
    "This letter serves as acknowledgment of a grant received from {dafOrg}. " +
    "This is not a tax receipt. The donor's charitable deduction was established " +
    "at the time of contribution to {dafOrg}.",
};

// ── Display Helpers ───────────────────────────────────────────────

/**
 * Format a dollar amount for display.
 * Returns "—" for null/undefined, "$X,XXX.XX" otherwise.
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * Get the display label for a gift type id.
 */
export const getGiftTypeLabel = (typeId, short = false) => {
  const type = GIFT_TYPE_MAP[typeId];
  if (!type) return typeId || "Unknown";
  return short ? type.shortLabel : type.label;
};

/**
 * Get the display label for a payment method id.
 */
export const getPaymentMethodLabel = (methodId) => {
  return PAYMENT_METHOD_MAP[methodId]?.label || methodId || "—";
};

/**
 * Resolve IRS language template for a gift, substituting values.
 */
export const resolveIrsLanguage = (gift, orgName) => {
  if (!gift) return "";

  let template;

  if (gift.gift_type === "cash") {
    template =
      gift.fair_market_value > 0
        ? IRS_LANGUAGE.cash_quid_pro_quo
        : IRS_LANGUAGE.cash_simple;
  } else {
    template = IRS_LANGUAGE[gift.gift_type] || IRS_LANGUAGE.cash_simple;
  }

  return template
    .replace("{orgName}", orgName || "this organization")
    .replace(
      "{dafOrg}",
      gift.daf_sponsoring_org || "the sponsoring organization"
    )
    .replace("{amount}", formatCurrency(gift.amount))
    .replace("{fmv}", formatCurrency(gift.fair_market_value))
    .replace("{deductible}", formatCurrency(gift.deductible_amount))
    .replace("{description}", gift.goods_services_description || "");
};

// ── Giving Level Helpers ─────────────────────────────────────────

/**
 * Resolve giving level name from chapter_settings.giving_levels JSONB
 * for a given amount. Returns null if no tiers configured.
 *
 * `scope` filters which tier set is consulted:
 *   - "perpetuity" (default): match against lifetime cumulative giving
 *   - "annual":              match against an annual giving window
 *
 * Tiers without a `scope` field are treated as "perpetuity" for
 * backward compatibility with any data created before scopes existed.
 */
export const resolveGivingLevel = (
  amount,
  givingLevels,
  scope = "perpetuity"
) => {
  if (!givingLevels || givingLevels.length === 0) return null;
  const value = amount || 0;

  const inScope = givingLevels.filter(
    (t) => (t.scope || "perpetuity") === scope
  );
  if (inScope.length === 0) return null;

  // Tiers are ordered ascending by min_amount
  const sorted = [...inScope].sort(
    (a, b) => (a.min_amount || 0) - (b.min_amount || 0)
  );

  let matched = null;
  for (const tier of sorted) {
    if (
      value >= (tier.min_amount || 0) &&
      (tier.max_amount === null ||
        tier.max_amount === undefined ||
        value <= tier.max_amount)
    ) {
      matched = tier.name;
    }
  }
  return matched;
};
